import React, { createContext, useContext, useReducer, useState } from 'react';
import { RecordingRules, RoomType } from '../types';
import { TwilioError } from 'twilio-video';
import { settingsReducer, initialSettings, Settings, SettingsAction } from './settings/settingsReducer';
import useActiveSinkId from './useActiveSinkId/useActiveSinkId';
import { useLocalStorageState } from '../hooks/useLocalStorageState/useLocalStorageState';
import usePasscodeAuth from './usePasscodeAuth/usePasscodeAuth';

type JoinTokenRequest = {
  joinKey?: string;
  verifyToken?: string;
  appointmentId?: string;
  dateOfBirth: string;
};

type JoinTokenResponse = {
  identity: { id: string; name: string };
  token: string;
  isHost: boolean;
};

type JoinRequestError = Error & {
  preventRedirect?: boolean;
};

const COACH_JOIN_ENDPOINT = '/api/appointments/coach-join';
const MEMBER_JOIN_ENDPOINT = '/api/appointments/member-join';
const RECORDING_RULES_ENDPOINT = '/api/appointments/recordingrules';
const APPOINTMENTS_BASE_ENDPOINT = '/api/appointments';
const CORRELATION_ID_HEADER = 'X-Correlation-Id';
const CORRELATION_ID_STORAGE_KEY = 'wy-correlation-id';

function generateCorrelationId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}

export function getCorrelationId(): string {
  try {
    const existing = window.sessionStorage.getItem(CORRELATION_ID_STORAGE_KEY);
    if (existing) {
      return existing;
    }
    const generated = generateCorrelationId();
    window.sessionStorage.setItem(CORRELATION_ID_STORAGE_KEY, generated);
    return generated;
  } catch {
    return generateCorrelationId();
  }
}

export function normalizeAppointmentsBaseEndpoint(configuredEndpoint?: string) {
  if (!configuredEndpoint) {
    return APPOINTMENTS_BASE_ENDPOINT;
  }

  const normalizedEndpoint = configuredEndpoint
    .trim()
    .replace(/\/+$/, '')
    .replace(/appointments(?=(coach|member)-join$)/, 'appointments/')
    .replace(/\/(coach|member)-join$/, '')
    .replace(/\/recordingrules$/, '');

  return normalizedEndpoint || APPOINTMENTS_BASE_ENDPOINT;
}

function resolveTokenEndpoint(isMemberFlow: boolean) {
  const configuredEndpoint = process.env.REACT_APP_TOKEN_ENDPOINT;
  const baseEndpoint = normalizeAppointmentsBaseEndpoint(configuredEndpoint);
  const desiredSuffix = (isMemberFlow ? MEMBER_JOIN_ENDPOINT : COACH_JOIN_ENDPOINT).replace(
    APPOINTMENTS_BASE_ENDPOINT,
    ''
  );
  return `${baseEndpoint}${desiredSuffix}`;
}

function resolveRecordingRulesEndpoint() {
  const configuredEndpoint = process.env.REACT_APP_TOKEN_ENDPOINT;
  const baseEndpoint = normalizeAppointmentsBaseEndpoint(configuredEndpoint);
  return `${baseEndpoint}${RECORDING_RULES_ENDPOINT.replace(APPOINTMENTS_BASE_ENDPOINT, '')}`;
}

async function readJoinResponse(response: Response) {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  const text = await response.text();
  return text ? { message: text } : null;
}

export function formatSessionDateTime(value?: string) {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export function buildJoinErrorMessage(
  isMemberFlow: boolean,
  message?: string,
  schedStartTime?: string,
  schedEndTime?: string
) {
  switch (message) {
    case 'SESSION_NOT_STARTED': {
      const formattedStart = formatSessionDateTime(schedStartTime);
      return formattedStart
        ? `Your session starts at ${formattedStart}. Please come back then.`
        : 'Your session has not started yet. Please come back then.';
    }
    case 'SESSION_ENDED': {
      const formattedEnd = formatSessionDateTime(schedEndTime);
      if (isMemberFlow) {
        return formattedEnd
          ? `Your session ended on ${formattedEnd}. Please message your coach if you need to reschedule.`
          : 'Your session has ended. Please message your coach if you need to reschedule.';
      }
      return formattedEnd
        ? `The session ended on ${formattedEnd}. Reschedule if needed.`
        : 'The session has ended. Reschedule if needed.';
    }
    case 'Invalid date of birth':
      return "We couldn't verify your information. Please try again.";
    case 'Join link not found or expired.':
      return "We couldn't verify your information. Please try again.";
    case 'TOO_MANY_ATTEMPTS':
      return 'Too many failed attempts. Please wait 15 minutes and try again.';
    default:
      return message || 'There was a problem joining the session. Please try again.';
  }
}

async function getJoinToken(request: JoinTokenRequest): Promise<JoinTokenResponse> {
  const isMemberFlow = Boolean(request.verifyToken || request.appointmentId);
  const endpoint = resolveTokenEndpoint(isMemberFlow);
  const payload = {
    ...(request.joinKey ? { join_key: request.joinKey } : {}),
    ...(request.verifyToken ? { verify_token: request.verifyToken } : {}),
    ...(request.appointmentId ? { appointment_id: request.appointmentId } : {}),
    patient_dob: request.dateOfBirth,
  };

  const correlationId = getCorrelationId();
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', [CORRELATION_ID_HEADER]: correlationId },
    body: JSON.stringify(payload),
  });

  const body = await readJoinResponse(response);
  console.info('[web-join] request complete', { correlationId, status: response.status, isMemberFlow });

  if (!response.ok) {
    const joinError = new Error(
      buildJoinErrorMessage(isMemberFlow, body?.message, body?.schedStartTime, body?.schedEndTime)
    ) as JoinRequestError;

    if ([400, 403, 429].includes(response.status)) {
      joinError.preventRedirect = true;
    }

    return Promise.reject(joinError);
  }

  return body as JoinTokenResponse;
}

export interface StateContextType {
  error: TwilioError | Error | null;
  setError(error: TwilioError | Error | null): void;
  getToken(request: JoinTokenRequest | string, roomName?: string): Promise<JoinTokenResponse>;
  user?: null | { displayName: undefined; photoURL: undefined; passcode?: string };
  signIn?(passcode?: string): Promise<void>;
  signOut?(): Promise<void>;
  isAuthReady?: boolean;
  isFetching: boolean;
  activeSinkId: string;
  setActiveSinkId(sinkId: string): void;
  settings: Settings;
  dispatchSetting: React.Dispatch<SettingsAction>;
  roomType?: RoomType;
  updateRecordingRules(room_sid: string, rules: RecordingRules): Promise<object>;
  isGalleryViewActive: boolean;
  setIsGalleryViewActive: React.Dispatch<React.SetStateAction<boolean>>;
  maxGalleryViewParticipants: number;
  setMaxGalleryViewParticipants: React.Dispatch<React.SetStateAction<number>>;
  isKrispEnabled: boolean;
  setIsKrispEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isKrispInstalled: boolean;
  setIsKrispInstalled: React.Dispatch<React.SetStateAction<boolean>>;
}

export const StateContext = createContext<StateContextType>(null!);

/*
  The 'react-hooks/rules-of-hooks' linting rules prevent React Hooks from being called
  inside of if() statements. This is because hooks must always be called in the same order
  every time a component is rendered. The 'react-hooks/rules-of-hooks' rule is disabled below
  because the "if (process.env.REACT_APP_SET_AUTH === 'firebase')" statements are evaluated
  at build time (not runtime). If the statement evaluates to false, then the code is not
  included in the bundle that is produced (due to tree-shaking). Thus, in this instance, it
  is ok to call hooks inside if() statements.
*/
export default function AppStateProvider(props: React.PropsWithChildren<{}>) {
  const [error, setError] = useState<TwilioError | Error | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isGalleryViewActive, setIsGalleryViewActive] = useLocalStorageState('gallery-view-active-key', true);
  const [activeSinkId, setActiveSinkId] = useActiveSinkId();
  const [settings, dispatchSetting] = useReducer(settingsReducer, initialSettings);
  const [roomType, setRoomType] = useState<RoomType>();
  const [maxGalleryViewParticipants, setMaxGalleryViewParticipants] = useLocalStorageState(
    'max-gallery-participants-key',
    6
  );

  const [isKrispEnabled, setIsKrispEnabled] = useState(false);
  const [isKrispInstalled, setIsKrispInstalled] = useState(false);

  let contextValue = {
    error,
    setError,
    isFetching,
    activeSinkId,
    setActiveSinkId,
    settings,
    dispatchSetting,
    roomType,
    isGalleryViewActive,
    setIsGalleryViewActive,
    maxGalleryViewParticipants,
    setMaxGalleryViewParticipants,
    isKrispEnabled,
    setIsKrispEnabled,
    isKrispInstalled,
    setIsKrispInstalled,
  } as StateContextType;

  if (process.env.REACT_APP_SET_AUTH === 'passcode') {
    contextValue = {
      ...contextValue,
      ...usePasscodeAuth(), // eslint-disable-line react-hooks/rules-of-hooks
    };
  } else {
    contextValue = {
      ...contextValue,
      getToken: request => getJoinToken(request as JoinTokenRequest),
      updateRecordingRules: async (room_sid, rules) => {
        const endpoint = resolveRecordingRulesEndpoint();

        return fetch(endpoint, {
          headers: { 'Content-Type': 'application/json', [CORRELATION_ID_HEADER]: getCorrelationId() },
          body: JSON.stringify({ room_sid, rules }),
          method: 'POST',
        })
          .then(async res => {
            const jsonResponse = await res.json();

            if (!res.ok) {
              const recordingError = new Error(
                jsonResponse.error?.message || 'There was an error updating recording rules'
              );
              recordingError.code = jsonResponse.error?.code;
              return Promise.reject(recordingError);
            }

            return jsonResponse;
          })
          .catch(err => setError(err));
      },
    };
  }

  const getToken: StateContextType['getToken'] = (request, roomName) => {
    setIsFetching(true);
    return contextValue
      .getToken(request as JoinTokenRequest, roomName)
      .then(res => {
        setRoomType('peer-to-peer');
        setIsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);

        return Promise.reject(err);
      });
  };

  const updateRecordingRules: StateContextType['updateRecordingRules'] = (room_sid, rules) => {
    setIsFetching(true);
    return contextValue
      .updateRecordingRules(room_sid, rules)
      .then(res => {
        setIsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  return (
    <StateContext.Provider value={{ ...contextValue, getToken, updateRecordingRules }}>
      {props.children}
    </StateContext.Provider>
  );
}

export function useAppState() {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useAppState must be used within the AppStateProvider');
  }
  return context;
}
